import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomListCard, BoardroomReportKey } from '../../../types';
import ListCardReport from './ListCardReport';
import useBoardRefresh from './useBoardRefresh';

/**
 * The full view of a table the room was asked for.
 *
 * It fetches its own rows rather than reading the page's `reportData`, because
 * the page holds ONE report at a time keyed by kind, and two `list` cards share
 * that kind. The card's `subject` is what tells them apart, so the fetch
 * belongs with the card.
 *
 * Cheap enough to do that way: no model call, no seat, no dig — the server
 * reads the graph it already loads for every other table on the board.
 */
export default function ListCardPane({
  boardroomId,
  subject,
  tags,
  verified,
  onOpen,
  onPickTag,
  refreshKey,
}: {
  boardroomId: number;
  subject: string;
  /** The lens the room has on, so an expanded card is cut the same way the
   *  rest of the screen is. */
  tags?: string[];
  verified?: string | null;
  onOpen?: (id: number) => void;
  /** A tag badge clicked: put it in the lens, or take it out. */
  onPickTag?: (key: string) => void;
  /** Changes whenever the page wants a refetch — the Reports tab passes the
   *  same trigger its built-in tables use, so this keeps pace with them. With
   *  none, the pane listens for itself (at most every 15 seconds). */
  refreshKey?: string | number;
}) {
  const [data, setData] = useState<BoardroomListCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lensKey = (tags ?? []).join('|');
  // Refetch while a dig lands findings, at most every 15 seconds.
  const fresh = useBoardRefresh(boardroomId, refreshKey === undefined);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .getBoardroomReport<BoardroomListCard>(boardroomId, 'list' as BoardroomReportKey, {
        subject,
        tags: tags ?? [],
        verified: verified ?? null,
      })
      .then(d => {
        if (alive) setData(d);
      })
      .catch(() => {
        // The card was dropped out from under the link, or the room is gone.
        if (alive) setError('This table is no longer pinned to the Dashboard.');
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardroomId, subject, lensKey, verified, fresh, refreshKey]);

  if (error) {
    return <div className="text-xs text-gray-500 dark:text-gray-400">{error}</div>;
  }
  if (!data) {
    return <div className="text-xs text-gray-400 dark:text-gray-500">Reading the board…</div>;
  }
  return <ListCardReport data={data} onOpen={onOpen} onPickTag={onPickTag} lensTags={tags} />;
}
